/* ==========================================================================
   Prénatal: Mix & Match offer on the product page (snippets/pn-mix-match.liquid)

   How pieces are chosen:
   1. Every product in the offer collection is put in a category (cot, dresser, wardrobe…) by the
      keywords from the snippet. Accessories matching an "exclude" keyword are left out.
   2. For each category other than the one being viewed, the in-stock products are ranked by how well
      their title matches this product: shared model names count most, shared finish/size less,
      and rarer words count more than common ones. So "Martha … Acacia" pulls the Martha Acacia
      dresser first, then other Martha pieces, then other finishes.
   3. Out of stock pieces are never offered, so the next best match takes their place. If the product
      being viewed is itself out of stock, an in-stock piece of the same category is offered instead.
   ========================================================================== */
(function () {
  if (customElements.get('pn-mix-match')) return;

  /* Words that say nothing about which model a piece is */
  const STOP_WORDS = [
    'the', 'and', 'for', 'with', 'baby', 'babies', 'kids', 'child', 'children', 'childrens', 'nursery',
    'bed', 'set', 'wood', 'wooden', 'beech', 'beechwood', 'wax', 'frame', 'base', 'design', 'premium',
    'convertible', 'adjustable', 'door', 'doors', 'drawer', 'drawers', 'chest', 'soft', 'close', 'natural',
    'wheels', 'spanish', 'micuna',
  ];
  /* Finishes, colours and sizes: worth matching, but less than the model name */
  const FINISH_WORDS = [
    'white', 'grey', 'gray', 'black', 'cream', 'beige', 'sand', 'oak', 'acacia', 'walnut',
    'pine', 'ash', 'waterwood', 'anthracite', 'pink', 'blue', 'green', 'big', 'mini', 'maxi',
  ];
  const NAME_WEIGHT = 3;

  const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const escapeHtml = (text) =>
    String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const keywordList = (text) =>
    text
      .split(',')
      .map((k) => k.trim().toLowerCase())
      .filter(Boolean);
  /* whole words only, so "cot" does not match "cotton"; a plural "s" is allowed */
  const keywordTest = (keywords) => {
    if (!keywords.length) return () => false;
    const pattern = new RegExp('(^|[^a-z0-9])(' + keywords.map(escapeRegExp).join('|') + ')s?($|[^a-z0-9])');
    return (text) => pattern.test(text);
  };
  const fill = (template, values) => template.replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  class PnMixMatch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      const source = this.querySelector('[data-pn-mm-config]');
      if (!source) return;
      try {
        this.config = JSON.parse(source.textContent);
      } catch (error) {
        return;
      }
      this.strings = this.config.strings;
      this.tiers = this.config.tiers.filter((tier) => tier.percent > 0).sort((a, b) => a.quantity - b.quantity);

      this.prepareProducts();
      this.current = this.products.find((p) => p.id === this.config.current.id);
      if (!this.current || this.current.category < 0 || !this.tiers.length) return;

      this.slots = this.buildSlots();
      const pieces = this.slots.length + (this.current.available ? 1 : 0);
      if (!this.slots.length || pieces < this.tiers[0].quantity) return;

      this.render();
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.update();
      this.hidden = false;
    }

    /* category, title words and how rare each word is across the collection */
    prepareProducts() {
      this.categories = this.config.categories
        .split('|')
        .map((entry) => {
          const [label, keywords = ''] = entry.split(':');
          return { label: label.trim(), test: keywordTest(keywordList(keywords)), words: keywordList(keywords) };
        })
        .filter((category) => category.label && category.words.length);
      const isExcluded = keywordTest(keywordList(this.config.exclude));

      const ignored = new Set(STOP_WORDS);
      this.categories.forEach((category) => category.words.join(' ').split(/\s+/).forEach((word) => ignored.add(word)));
      const finishes = new Set(FINISH_WORDS);

      /* a sold out product is no longer in the collection, so the one being viewed is always added back */
      const listed = this.config.products.filter((product) => product.id !== this.config.current.id);
      this.products = [...listed, this.config.current].map((product, index) => {
        const text = (product.title + ' ' + product.type).toLowerCase();
        const words = product.title
          .toLowerCase()
          .replace(/['’]/g, '')
          .split(/[^a-z0-9]+/)
          .filter((word) => word.length > 2 && !ignored.has(word) && !ignored.has(word.replace(/s$/, '')));
        return {
          ...product,
          index,
          category: isExcluded(text) ? -1 : this.categories.findIndex((category) => category.test(text)),
          words: new Set(words),
        };
      });

      const counts = {};
      this.products.forEach((product) => product.words.forEach((word) => (counts[word] = (counts[word] || 0) + 1)));
      this.wordWeight = (word) => {
        const rarity = Math.log(this.products.length / counts[word]) + 0.1;
        return finishes.has(word) || /\d/.test(word) ? rarity : rarity * NAME_WEIGHT;
      };
    }

    matchScore(product) {
      let score = 0;
      this.current.words.forEach((word) => {
        if (product.words.has(word)) score += this.wordWeight(word);
      });
      return score;
    }

    /* one slot per category, each holding its in-stock candidates, best match first */
    buildSlots() {
      const slots = [];
      this.categories.forEach((category, categoryIndex) => {
        const sameCategory = categoryIndex === this.current.category;
        if (sameCategory && this.current.available) return;

        const candidates = this.products
          .filter((p) => p.category === categoryIndex && p.available && p.id !== this.current.id)
          .map((p) => ({ product: p, score: this.matchScore(p) }))
          .sort((a, b) => b.score - a.score || a.product.index - b.product.index);
        if (!candidates.length) return;

        slots.push({
          category,
          categoryIndex,
          sameCategory,
          topScore: candidates[0].score,
          candidates: candidates.map((candidate) => candidate.product),
          position: 0,
          selected: false,
        });
      });
      /* the replacement for a sold out product comes first, then the closest matches */
      return slots.sort(
        (a, b) => b.sameCategory - a.sameCategory || b.topScore - a.topScore || a.categoryIndex - b.categoryIndex
      );
    }

    money(cents) {
      const format = (window.MinimogSettings && window.MinimogSettings.money_format) || '${{amount}}';
      if (typeof window.formatMoney === 'function') return window.formatMoney(cents, format);
      return '$' + (cents / 100).toFixed(2);
    }

    rowHtml(product, options) {
      const image = product.image
        ? `<img class="pn-mm__image" src="${escapeHtml(product.image)}" alt="" width="64" height="64" loading="lazy">`
        : '<span class="pn-mm__image"></span>';
      return `
        <label class="pn-mm__pick">
          <input type="checkbox" class="pn-mm__checkbox" ${options.checkbox}>
          <span class="pn-mm__check" aria-hidden="true"></span>
          ${image}
          <span class="pn-mm__info">
            <span class="pn-mm__label">${escapeHtml(options.label)}</span>
            <span class="pn-mm__name">${escapeHtml(product.title)}</span>
            <span class="pn-mm__price" data-pn-mm-price></span>
          </span>
        </label>`;
    }

    slotHtml(slot, slotIndex) {
      const product = slot.candidates[slot.position];
      const category = slot.category.label.toLowerCase();
      const label = slot.sameCategory ? fill(this.strings.alternative, { category }) : slot.category.label;
      const stepper =
        slot.candidates.length > 1
          ? `<span class="pn-mm__stepper" role="group" aria-label="${escapeHtml(fill(this.strings.change, { category }))}">
              <button type="button" class="pn-mm__step" data-pn-mm-step="-1" aria-label="${escapeHtml(this.strings.previous)}">&lsaquo;</button>
              <span class="pn-mm__count">${slot.position + 1}/${slot.candidates.length}</span>
              <button type="button" class="pn-mm__step" data-pn-mm-step="1" aria-label="${escapeHtml(this.strings.next)}">&rsaquo;</button>
            </span>`
          : '';
      return `
        ${this.rowHtml(product, { label, checkbox: `data-pn-mm-slot="${slotIndex}"${slot.selected ? ' checked' : ''}` })}
        <span class="pn-mm__side">
          ${stepper}
          <a class="pn-mm__view" href="${escapeHtml(product.url)}" aria-label="${escapeHtml(product.title)}">View</a>
        </span>`;
    }

    render() {
      const pieces = this.slots.length + (this.current.available ? 1 : 0);
      const tiers = this.tiers
        .map((tier, index) => {
          const orMore = index === this.tiers.length - 1 && pieces > tier.quantity;
          return `<li class="pn-mm__tier" data-pn-mm-tier="${tier.quantity}">${escapeHtml(
            fill(orMore ? this.strings.tierOrMore : this.strings.tier, { count: tier.quantity, percent: tier.percent })
          )}</li>`;
        })
        .join('');
      const currentRow = this.current.available
        ? `<li class="pn-mm__row pn-mm__row--current" data-pn-mm-row="current">${this.rowHtml(this.current, {
            label: this.strings.thisItem,
            checkbox: 'checked disabled',
          })}</li>`
        : '';
      const slotRows = this.slots
        .map((slot, index) => `<li class="pn-mm__row" data-pn-mm-row="${index}">${this.slotHtml(slot, index)}</li>`)
        .join('');

      this.insertAdjacentHTML(
        'beforeend',
        `<div class="pn-mm__head">
          <span class="pn-mm__eyebrow">${escapeHtml(this.strings.eyebrow)}</span>
          <p class="pn-mm__title">${escapeHtml(this.current.available ? this.strings.title : this.strings.soldOutTitle)}</p>
          <ul class="pn-mm__tiers" role="list">${tiers}</ul>
        </div>
        <ul class="pn-mm__rows" role="list">${currentRow}${slotRows}</ul>
        <div class="pn-mm__foot" aria-live="polite">
          <p class="pn-mm__hint" data-pn-mm-hint></p>
          <div class="pn-mm__summary" data-pn-mm-summary hidden>
            <p class="pn-mm__total"><s data-pn-mm-was></s> <strong data-pn-mm-now></strong></p>
            <p class="pn-mm__saving" data-pn-mm-saving></p>
            <button type="button" class="pn-mm__add" data-pn-mm-add></button>
            <p class="pn-mm__note">${escapeHtml(this.strings.note)}</p>
          </div>
          <p class="pn-mm__error" data-pn-mm-error role="alert" hidden></p>
        </div>`
      );
    }

    onChange(event) {
      const checkbox = event.target.closest('[data-pn-mm-slot]');
      if (!checkbox) return;
      this.slots[Number(checkbox.dataset.pnMmSlot)].selected = checkbox.checked;
      this.update();
    }

    onClick(event) {
      const step = event.target.closest('[data-pn-mm-step]');
      if (step) {
        const row = step.closest('[data-pn-mm-row]');
        const slotIndex = Number(row.dataset.pnMmRow);
        const slot = this.slots[slotIndex];
        const direction = Number(step.dataset.pnMmStep);
        slot.position = (slot.position + direction + slot.candidates.length) % slot.candidates.length;
        row.innerHTML = this.slotHtml(slot, slotIndex);
        row.querySelector(`[data-pn-mm-step="${direction}"]`).focus();
        this.update();
        return;
      }
      if (event.target.closest('[data-pn-mm-add]')) this.addToCart();
    }

    /* [{ row, product }] for every piece currently ticked */
    selection() {
      const picked = [];
      if (this.current.available) picked.push({ row: 'current', product: this.current });
      this.slots.forEach((slot, index) => {
        if (slot.selected) picked.push({ row: String(index), product: slot.candidates[slot.position] });
      });
      return picked;
    }

    update() {
      const picked = this.selection();
      const tier = this.tiers.filter((t) => picked.length >= t.quantity).pop();
      const nextTier = this.tiers.find((t) => picked.length < t.quantity);
      const percent = tier ? tier.percent : 0;
      const reduced = (price) => Math.round((price * (100 - percent)) / 100);
      const pickedRows = new Set(picked.map((item) => item.row));

      this.querySelectorAll('[data-pn-mm-row]').forEach((row) => {
        const key = row.dataset.pnMmRow;
        const product = key === 'current' ? this.current : this.slots[Number(key)].candidates[this.slots[Number(key)].position];
        const discounted = percent > 0 && pickedRows.has(key);
        row.classList.toggle('pn-mm__row--picked', pickedRows.has(key));
        row.querySelector('[data-pn-mm-price]').innerHTML = discounted
          ? `<s>${this.money(product.price)}</s> <strong>${this.money(reduced(product.price))}</strong>`
          : this.money(product.price);
      });
      this.querySelectorAll('[data-pn-mm-tier]').forEach((chip) => {
        chip.classList.toggle('pn-mm__tier--active', Boolean(tier) && Number(chip.dataset.pnMmTier) === tier.quantity);
      });

      const hint = this.querySelector('[data-pn-mm-hint]');
      const possible = this.slots.length + (this.current.available ? 1 : 0);
      if (nextTier && possible >= nextTier.quantity) {
        hint.textContent = fill(tier ? this.strings.hintNext : this.strings.hintFirst, { percent: nextTier.percent });
        hint.hidden = false;
      } else {
        hint.hidden = true;
      }

      const summary = this.querySelector('[data-pn-mm-summary]');
      summary.hidden = !tier;
      if (tier) {
        const was = picked.reduce((sum, item) => sum + item.product.price, 0);
        const now = picked.reduce((sum, item) => sum + reduced(item.product.price), 0);
        summary.querySelector('[data-pn-mm-was]').innerHTML = this.money(was);
        summary.querySelector('[data-pn-mm-now]').innerHTML = this.money(now);
        summary.querySelector('[data-pn-mm-saving]').innerHTML = fill(this.strings.saving, {
          amount: this.money(was - now),
          percent,
        });
        summary.querySelector('[data-pn-mm-add]').textContent = fill(this.strings.add, { count: picked.length });
      }
      this.querySelector('[data-pn-mm-error]').hidden = true;
    }

    addToCart() {
      const button = this.querySelector('[data-pn-mm-add]');
      const errorBox = this.querySelector('[data-pn-mm-error]');
      const settings = window.MinimogSettings || {};
      const drawer = document.querySelector('m-cart-drawer');
      const useDrawer = Boolean(settings.use_ajax_atc && settings.enable_cart_drawer && drawer && drawer.getSectionsToRender);
      const cartAddUrl = (settings.routes && settings.routes.cart_add_url) || '/cart/add';
      const cartUrl = (settings.routes && settings.routes.cart) || '/cart';

      const body = { items: this.selection().map((item) => ({ id: item.product.variantId, quantity: 1 })) };
      if (useDrawer) {
        body.sections = drawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      const label = button.textContent;
      button.disabled = true;
      button.textContent = this.strings.adding;
      errorBox.hidden = true;

      fetch(cartAddUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((cart) => {
          if (cart.status) throw new Error(cart.description || cart.message);
          if (!useDrawer) {
            window.location = cartUrl;
            return;
          }
          drawer.renderContents(cart);
          drawer.classList.remove('m-cart--empty');
          const items = drawer.querySelector('m-cart-drawer-items');
          if (items) items.classList.remove('m-cart--empty');
          if (window.MinimogEvents && window.MinimogTheme) {
            window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, cart);
            window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.openCartDrawer);
          }
        })
        .catch((error) => {
          errorBox.textContent = (error && error.message) || this.strings.error;
          errorBox.hidden = false;
        })
        .finally(() => {
          button.disabled = false;
          button.textContent = label;
        });
    }
  }

  customElements.define('pn-mix-match', PnMixMatch);
})();
